/**
 * Single source of truth for flight booking REVIEW display money.
 *
 * Prefer supplier-native / resolved offer currency (USD when offer is USD).
 * Never invent ILS from site defaults, FX widgets, or footer-only formatters.
 */

function finitePositive(...candidates) {
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function normalizeCurrency(raw) {
  const c = String(raw || "").trim().toUpperCase();
  if (!c) return "";
  if (c === "$" || c === "US$" || c === "USD") return "USD";
  if (c === "₪" || c === "NIS" || c === "ILS") return "ILS";
  return c;
}

function currencySymbol(currency) {
  const c = normalizeCurrency(currency);
  if (c === "USD") return "$";
  if (c === "ILS") return "₪";
  return c || "";
}

function isUnifiedOrDuffelOffer(offer) {
  if (!offer || typeof offer !== "object") return false;
  if (offer.source === "unified" || offer.__unified === true) return true;
  return String(offer.provider || "").toLowerCase() === "duffel";
}

function isIlsCurrency(currency) {
  const c = normalizeCurrency(currency);
  return c === "ILS";
}

/**
 * Format amount in supplier-native currency for review / footer / sidebar.
 * @param {number|null|undefined} amount
 * @param {string|null|undefined} currency
 * @returns {string|null}
 */
export function formatFlightReviewDisplayMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return null;
  const c = normalizeCurrency(currency);
  const rounded =
    Math.abs(n - Math.round(n)) < 0.001
      ? Math.round(n)
      : Math.round(n * 100) / 100;
  const hasFraction = Math.abs(rounded - Math.round(rounded)) > 0.001;
  const locale = c === "ILS" ? "he-IL" : "en-US";
  const numStr = rounded.toLocaleString(locale, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
  if (c === "USD") return `$${numStr}`;
  if (c === "ILS") return `₪${numStr}`;
  if (c === "EUR") return `€${numStr}`;  // ← חדש!
  if (c === "GBP") return `£${numStr}`;  // ← חדש!
  return c ? `${numStr} ${c}` : numStr;
}

/**
 * Footer line for SummaryFooter override — same amount+currency as body/sidebar.
 * ILS keeps legacy dual line `N₪ ($M)` when usdCompanion is provided.
 * Non-ILS: single native line only (no silent ILS conversion).
 *
 * @param {{ displayAmount: number|null, displayCurrency: string, formattedLabel?: string|null, usdCompanion?: number|null }} p
 * @returns {string|null}
 */
export function formatFlightReviewFooterPriceText({
  displayAmount,
  displayCurrency,
  formattedLabel = null,
  usdCompanion = null,
} = {}) {
  const c = normalizeCurrency(displayCurrency);
  const amount = finitePositive(displayAmount);

  if (c === "ILS" && amount != null) {
    const ilsNum = Math.ceil(amount - 1e-9).toLocaleString("he-IL");
    const usd = finitePositive(usdCompanion);
    if (usd != null) {
      const usdRounded = Math.ceil(usd - 1e-9);
      return `${ilsNum}₪ ($${usdRounded.toLocaleString("en-US")})`;
    }
    return `${ilsNum}₪`;
  }

  if (formattedLabel) return formattedLabel;
  if (amount == null) return null;
  return formatFlightReviewDisplayMoney(amount, c || "USD");
}

/**
 * Resolve review display money from selected/resolved offer + booking pricing.
 *
 * Priority:
 * 1) pricing.total when pricing.currency is set (BookingFlow SSOT)
 * 2) Unified/Duffel provider-native (resolved currentPrice / providerPrice*)
 * 3) LDS client ILS fields when present
 * 4) Offer labels as last resort
 *
 * @param {{ selectedFlightOffer?: object|null, pricing?: object|null }} p
 * @returns {{
 *   displayAmount: number|null,
 *   displayCurrency: string,
 *   displayCurrencySymbol: string,
 *   formattedLabel: string|null,
 *   source: string,
 *   isProviderNative: boolean,
 * }}
 */
export function resolveFlightReviewDisplayMoney({
  selectedFlightOffer = null,
  pricing = null,
} = {}) {
  const offer = selectedFlightOffer && typeof selectedFlightOffer === "object"
    ? selectedFlightOffer
    : null;
  const pricingCurrency = normalizeCurrency(pricing?.currency);
  const pricingTotal = finitePositive(pricing?.total);
  const unified = isUnifiedOrDuffelOffer(offer);

  const providerAmount = finitePositive(
    offer?.resolvedPrice?.amount,
    offer?.price?.amount,
    offer?.providerPriceAmount,
    offer?.rawTotalFare,
    offer?.totalFare
  );
  const providerCurrency = normalizeCurrency(
    offer?.resolvedPrice?.currency ||
      offer?.price?.currency ||
      offer?.providerPriceCurrency ||
      offer?.rawCurrency
  );

  // BookingFlow pricing is authoritative when currency is explicit.
  if (pricingTotal != null && pricingCurrency) {
    // Guard: never treat a USD provider total as ILS just because currency was blank upstream.
    if (
      unified &&
      isIlsCurrency(pricingCurrency) &&
      providerCurrency &&
      !isIlsCurrency(providerCurrency) &&
      providerAmount != null &&
      Math.abs(pricingTotal - providerAmount) < 0.02
    ) {
      const label = formatFlightReviewDisplayMoney(providerAmount, providerCurrency);
      return {
        displayAmount: providerAmount,
        displayCurrency: providerCurrency,
        displayCurrencySymbol: currencySymbol(providerCurrency),
        formattedLabel: label,
        source: "provider_native_guard",
        isProviderNative: true,
      };
    }
    const label = formatFlightReviewDisplayMoney(pricingTotal, pricingCurrency);
    return {
      displayAmount: pricingTotal,
      displayCurrency: pricingCurrency,
      displayCurrencySymbol: currencySymbol(pricingCurrency),
      formattedLabel: label,
      source: String(pricing?.source || "pricing"),
      isProviderNative: unified && !isIlsCurrency(pricingCurrency),
    };
  }

  // Pricing total present but currency missing — prefer provider currency for unified.
  if (pricingTotal != null && unified && providerCurrency) {
    const label = formatFlightReviewDisplayMoney(pricingTotal, providerCurrency);
    return {
      displayAmount: pricingTotal,
      displayCurrency: providerCurrency,
      displayCurrencySymbol: currencySymbol(providerCurrency),
      formattedLabel: label,
      source: "pricing_total_provider_currency",
      isProviderNative: true,
    };
  }

  if (providerAmount != null && providerCurrency) {
    const label =
      formatFlightReviewDisplayMoney(providerAmount, providerCurrency) ||
      offer?.providerPriceLabel ||
      offer?.priceDisplayLabel ||
      offer?.totalFareDisplayLabel ||
      null;
    return {
      displayAmount: providerAmount,
      displayCurrency: providerCurrency,
      displayCurrencySymbol: currencySymbol(providerCurrency),
      formattedLabel: label,
      source: "provider_native",
      isProviderNative: true,
    };
  }

  const clientIls = finitePositive(
    offer?.priceClientTotalIls,
    pricingTotal
  );
  if (clientIls != null) {
    const label = formatFlightReviewDisplayMoney(clientIls, "ILS");
    return {
      displayAmount: clientIls,
      displayCurrency: "ILS",
      displayCurrencySymbol: "₪",
      formattedLabel: label,
      source: "client_ils",
      isProviderNative: false,
    };
  }

  const fallbackLabel =
    offer?.priceDisplayLabel ||
    offer?.totalFareDisplayLabel ||
    offer?.providerPriceLabel ||
    null;

  return {
    displayAmount: null,
    displayCurrency: providerCurrency || pricingCurrency || "",
    displayCurrencySymbol: currencySymbol(providerCurrency || pricingCurrency),
    formattedLabel: fallbackLabel,
    source: "label_fallback",
    isProviderNative: Boolean(unified && providerCurrency),
  };
}
